import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { getCachedCoords, requestCoords } from '../utils/geolocation';

const CATEGORIES = [
  { id: 'all', label: 'All Services' },
  { id: 'mechanics', label: 'Mechanics' },
  { id: 'tuners', label: 'Specialised Tuners' },
  { id: 'maintenance', label: 'Car Service & Maintenance' },
  { id: 'auto_electrician', label: 'Auto Electrician' },
  { id: 'paint_wrap_detail', label: 'Paint, Wraps & Detailers' },
  { id: 'tyres_wheels', label: 'Tyres & Wheels' },
  { id: 'suspension_brakes', label: 'Suspension & Brakes' },
  { id: 'exhaust_fab', label: 'Exhaust & Fabrication' },
  { id: 'body_repair', label: 'Body Repair (Smash/Panel)' },
  { id: 'audio_electronics', label: 'Audio & Electronics' },
];

const POPULAR_MAKES = [
  'Volkswagen', 'Audi', 'BMW', 'Mercedes-Benz', 'Porsche', 'Skoda', 'SEAT',
  'Toyota', 'Honda', 'Mazda', 'Subaru', 'Nissan', 'Hyundai', 'Kia',
  'Ford', 'Holden', 'Chevrolet', 'Dodge', 'Jeep', 'Ram',
  'Tesla', 'Volvo', 'Jaguar', 'Land Rover', 'Mini', 'Peugeot', 'Renault',
  'Mitsubishi', 'Suzuki', 'Lexus', 'Acura', 'Infiniti', 'Genesis',
];

const formatDistance = (m) => {
  if (typeof m !== 'number') return null;
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m)} m`;
};

const ratingPercent = (rating) => {
  const r = typeof rating === 'number' ? rating : 0;
  return Math.max(0, Math.min(1, r / 5));
};

const PlacePhoto = ({ src, alt, onError }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <div className="services-card-media" aria-hidden="true">
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        onError={() => {
          setFailed(true);
          onError?.();
        }}
      />
    </div>
  );
};

const Services = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialCat = searchParams.get('cat') || 'all';
  const [activeCat, setActiveCat] = useState(CATEGORIES.some(c => c.id === initialCat) ? initialCat : 'all');

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [q, setQ] = useState(searchParams.get('q') || '');
  const [make, setMake] = useState(searchParams.get('make') || '');
  const [model, setModel] = useState(searchParams.get('model') || '');
  const [radiusKm, setRadiusKm] = useState(Number(searchParams.get('radiusKm') || 10));
  const [openNow, setOpenNow] = useState(searchParams.get('openNow') === 'true');
  const [minRating, setMinRating] = useState(searchParams.get('minRating') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'recommended');

  const [geo, setGeo] = useState(null); // { lat, lng }
  const [geoStatus, setGeoStatus] = useState('idle'); // idle|loading|granted|denied|error
  const [geoError, setGeoError] = useState('');

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [brokenPhotos, setBrokenPhotos] = useState(() => new Set());

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const selectedPlaceIdRef = useRef(null);

  const canSearchWithoutGeo = useMemo(() => {
    const anyText = [q, make, model].some(v => (v || '').trim().length > 0);
    return anyText;
  }, [q, make, model]);

  const syncUrl = useCallback((extra = {}) => {
    const next = new URLSearchParams(searchParams);
    next.set('cat', activeCat);
    if (q.trim()) next.set('q', q.trim()); else next.delete('q');
    if (make.trim()) next.set('make', make.trim()); else next.delete('make');
    if (model.trim()) next.set('model', model.trim()); else next.delete('model');
    next.set('radiusKm', String(radiusKm || 10));
    next.set('openNow', openNow ? 'true' : 'false');
    if (minRating) next.set('minRating', String(minRating)); else next.delete('minRating');
    next.set('sort', sort || 'distance');

    Object.entries(extra).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, String(v));
    });

    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, activeCat, q, make, model, radiusKm, openNow, minRating, sort]);

  const requestGeo = useCallback(async () => {
    setGeoStatus('loading');
    setGeoError('');
    try {
      const pos = await requestCoords();
      setGeo({ lat: pos.lat, lng: pos.lng });
      setGeoStatus('granted');
    } catch (err) {
      setGeo(null);
      const denied = err?.code === 1 || err?.code === 'PERMISSION_DENIED';
      setGeoStatus(denied ? 'denied' : 'error');
      setGeoError(err?.message || 'Unable to access your location.');
    }
  }, []);

  const load = useCallback(async () => {
    if (!geo && !canSearchWithoutGeo) {
      setResults([]);
      setMeta(null);
      return;
    }

    setLoading(true);
    try {
      const params = {
        cat: activeCat,
        q: q.trim() || undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
        radiusKm: geo ? radiusKm : undefined,
        openNow: openNow ? 'true' : 'false',
        minRating: minRating ? minRating : undefined,
        sort,
        limit: 40,
        lat: geo?.lat,
        lng: geo?.lng,
      };

      const { data } = await api.get('/services/search', { params });
      setResults(data.results || []);
      setMeta(data.meta || null);
      setBrokenPhotos(new Set());
    } catch (err) {
      setResults([]);
      setMeta(null);
      toast.error(err.response?.data?.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, [geo, canSearchWithoutGeo, activeCat, q, make, model, radiusKm, openNow, minRating, sort]);

  // Keep URL in sync as the user changes controls
  useEffect(() => {
    syncUrl();
  }, [syncUrl]);

  // Default behavior: use location automatically (reuse cached coords if available)
  useEffect(() => {
    const cached = getCachedCoords();
    if (cached) {
      setGeo(cached);
      setGeoStatus('granted');
      return;
    }
    requestGeo();
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [load]);

  const openDetails = useCallback(async (placeId) => {
    if (!placeId) return;
    selectedPlaceIdRef.current = placeId;
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetails(null);
    syncUrl({ placeId });

    try {
      const { data } = await api.get(`/services/details/${encodeURIComponent(placeId)}`);
      // Only apply if still the selected place
      if (selectedPlaceIdRef.current === placeId) setDetails(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load service details');
    } finally {
      if (selectedPlaceIdRef.current === placeId) setDetailsLoading(false);
    }
  }, [syncUrl]);

  const closeDetails = useCallback(() => {
    selectedPlaceIdRef.current = null;
    setDetailsOpen(false);
    setDetails(null);
    setDetailsLoading(false);
    syncUrl({ placeId: null });
  }, [syncUrl]);

  // Deep-link: ?placeId=...
  useEffect(() => {
    const pid = searchParams.get('placeId');
    if (pid && pid !== selectedPlaceIdRef.current) {
      openDetails(pid);
    }
  }, [searchParams, openDetails]);

  return (
    <div className="container page">
      <div className="services-header">
        <div className="services-header-actions">
          <button className="btn btn-secondary btn-sm services-sidebar-toggle" onClick={() => setSidebarOpen(true)}>Categories</button>
          <button className="btn btn-primary btn-sm" onClick={requestGeo} disabled={geoStatus === 'loading'}>
            {geoStatus === 'loading' ? 'Locating…' : (geo ? 'Update location' : 'Use my location')}
          </button>
        </div>
      </div>

      {(geoStatus === 'denied' || geoStatus === 'error') && (
        <div className="services-location-hint">
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Location unavailable</div>
          <div className="text-muted">
            {geoError || 'Enable location permissions to see nearby services. You can still search by keyword, but results may be less relevant.'}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" onClick={requestGeo}>Retry location</button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setGeo(null); setGeoStatus('idle'); setGeoError(''); }}>Dismiss</button>
          </div>
        </div>
      )}

      <div className="services-layout">
        {/* Mobile overlay */}
        {sidebarOpen && <div className="services-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

        <aside className={`services-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="services-sidebar-header">
            <div style={{ fontWeight: 800 }}>Categories</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setSidebarOpen(false)}>Close</button>
          </div>

          <div className="services-sidebar-list">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                className={`services-cat ${activeCat === c.id ? 'active' : ''}`}
                onClick={() => { setActiveCat(c.id); setSidebarOpen(false); }}
              >
                {c.label}
              </button>
            ))}
          </div>
        </aside>

        <section className="services-main">
          <div className="services-filters card">
            <div className="services-filters-row">
              <div className="services-filter">
                <label>Search term</label>
                <input className="input" value={q} onChange={e => setQ(e.target.value)} placeholder="e.g. DSG service, ceramic coating, dyno…" />
              </div>
              <div className="services-filter">
                <label>Make specialist</label>
                <input className="input" list="services-makes" value={make} onChange={e => setMake(e.target.value)} placeholder="e.g. Volkswagen" />
                <datalist id="services-makes">
                  {POPULAR_MAKES.map(m => <option key={m} value={m} />)}
                </datalist>
              </div>
              <div className="services-filter">
                <label>Model focus</label>
                <input className="input" value={model} onChange={e => setModel(e.target.value)} placeholder="e.g. Golf GTI, EA888, DSG" />
              </div>
            </div>

            <div className="services-filters-row services-filters-row--compact">
              <div className="services-filter services-filter--sm">
                <label>Radius</label>
                <select className="input" value={radiusKm} onChange={e => setRadiusKm(Number(e.target.value))} disabled={!geo}>
                  {[5, 10, 25, 50].map(v => <option key={v} value={v}>{v} km</option>)}
                </select>
              </div>
              <div className="services-filter services-filter--sm">
                <label>Min rating</label>
                <select className="input" value={minRating} onChange={e => setMinRating(e.target.value)}>
                  <option value="">Any</option>
                  <option value="4">4.0+</option>
                  <option value="4.5">4.5+</option>
                </select>
              </div>
              <div className="services-filter services-filter--sm">
                <label>Sort</label>
                <select className="input" value={sort} onChange={e => setSort(e.target.value)}>
                  <option value="recommended">Recommended</option>
                  <option value="distance">Distance</option>
                  <option value="rating">Rating</option>
                </select>
              </div>
              <div className="services-filter services-filter--toggle">
                <label>Open now</label>
                <button className={openNow ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'} onClick={() => setOpenNow(v => !v)} type="button">
                  {openNow ? 'On' : 'Off'}
                </button>
              </div>
              {!geo && (
                <div className="services-filter services-filter--note">
                  <div className="text-muted">Enable location for accurate nearby results.</div>
                </div>
              )}
            </div>
          </div>

          <div className="services-results">
            {loading ? (
              <div className="empty-state" style={{ paddingTop: 24 }}>
                <div className="empty-icon">…</div>
                <h3>Searching</h3>
                <p className="text-muted">Finding nearby services…</p>
              </div>
            ) : (!geo && !canSearchWithoutGeo) ? (
              <div className="empty-state" style={{ paddingTop: 24 }}>
                <div className="empty-icon">⌁</div>
                <h3>Search services</h3>
                <p className="text-muted">Use your location, or type a keyword (e.g. “Volkswagen tuner”).</p>
              </div>
            ) : results.length === 0 ? (
              <div className="empty-state" style={{ paddingTop: 24 }}>
                <div className="empty-icon">—</div>
                <h3>No results</h3>
                <p className="text-muted">Try a different category, broaden radius, or remove filters.</p>
              </div>
            ) : (
              <div className="services-cards">
                {results.map((r) => {
                  const dist = formatDistance(r.distanceMeters);
                  const hasPhoto = !!r.photoUrl && !brokenPhotos.has(r.placeId);
                  return (
                    <div key={r.placeId} className={`services-card card${hasPhoto ? ' services-card--withMedia' : ''}`}>
                      {hasPhoto && (
                        <PlacePhoto
                          src={r.photoUrl}
                          alt={r.name}
                          onError={() => setBrokenPhotos(prev => {
                            const next = new Set(prev);
                            next.add(r.placeId);
                            return next;
                          })}
                        />
                      )}
                      <div className="services-card-body">
                        <div className="services-card-top">
                          <div className="services-card-title">
                            <div className="services-card-nameRow">
                              <div className="services-name" style={{ fontWeight: 800 }}>{r.name || 'Unknown'}</div>
                              {r.openNow === true && <span className="services-open-pill services-open-pill--open">Open</span>}
                              {r.openNow === false && <span className="services-open-pill">Closed</span>}
                            </div>
                            <div className="services-locationRow">
                              <div className="text-muted services-locationText" style={{ fontSize: '0.85rem' }}>
                                {r.address || '—'}
                              </div>
                              {dist && <div className="services-distance">{dist}</div>}
                            </div>
                          </div>
                          {typeof r.rating === 'number' && (
                            <div className="services-rating" title={`${r.rating.toFixed(1)} / 5`}>
                              <div className="services-rating-top">
                                <span className="services-rating-value">{r.rating.toFixed(1)}</span>
                                <span className="services-rating-count">({r.userRatingsTotal ?? 0})</span>
                              </div>
                              <div className="services-rating-bar" aria-hidden="true">
                                <div className="services-rating-bar-fill" style={{ width: `${ratingPercent(r.rating) * 100}%` }} />
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="services-card-actions">
                          <div className="services-card-actions-left">
                            <button className="btn btn-primary btn-sm" onClick={() => openDetails(r.placeId)}>Details</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      {detailsOpen && (
        <div className="modal-overlay" onClick={closeDetails}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720, minHeight: 0 }}>
            <div className="modal-header">
              <h3>{details?.name || 'Service Details'}</h3>
              <button className="close-btn" onClick={closeDetails}>×</button>
            </div>
            <div className="modal-body">
              {detailsLoading ? (
                <div className="empty-state" style={{ paddingTop: 12 }}>
                  <div className="empty-icon">…</div>
                  <h3>Loading</h3>
                  <p className="text-muted">Fetching details…</p>
                </div>
              ) : !details ? (
                <p className="text-muted">No details available.</p>
              ) : (
                <div className="services-details">
                  {details.address && (
                    <div className="services-detail-row">
                      <div className="services-detail-label">Address</div>
                      <div>{details.address}</div>
                    </div>
                  )}
                  {(details.phone || details.phoneInternational) && (
                    <div className="services-detail-row">
                      <div className="services-detail-label">Phone</div>
                      <div>
                        {details.phone || details.phoneInternational}
                      </div>
                    </div>
                  )}
                  {details.website && (
                    <div className="services-detail-row">
                      <div className="services-detail-label">Website</div>
                      <div>
                        <a href={details.website} target="_blank" rel="noopener noreferrer">{details.website}</a>
                      </div>
                    </div>
                  )}
                  {details.openingHours?.weekdayText?.length > 0 && (
                    <div className="services-detail-row">
                      <div className="services-detail-label">Hours</div>
                      <div className="services-hours">
                        {details.openingHours.weekdayText.map((t) => (
                          <div key={t} className="text-muted">{t}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
                    {details.googleMapsUrl && (
                      <a href={details.googleMapsUrl} target="_blank" rel="noopener noreferrer"
                        style={{ background: '#fff', color: '#111', border: '1px solid #fff', padding: '8px 18px', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}>
                        Open in Google Maps ↗
                      </a>
                    )}
                  </div>

                  <div className="text-muted" style={{ marginTop: 18, fontSize: '0.8rem', opacity: 0.85 }}>
                    Powered by Google Places
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
